import { useEffect, useState } from "react";
import { emptySessionData, SessionData, STEPS, StepKey } from "./types";
import { StepIndicator } from "./StepIndicator";
import { ContactInfoStep } from "./ContactInfoStep";
import { DocumentUploadStep } from "./DocumentUploadStep";
import { ReviewStep } from "./ReviewStep";
import { HowItWorksStep } from "./HowItWorksStep";
import { ConfirmationStep } from "./ConfirmationStep";

interface Props {
  onClose: () => void;
}

export function Wizard({ onClose }: Props) {
  const [data, setData] = useState<SessionData>(emptySessionData);
  const [stepIdx, setStepIdx] = useState(0);

  const update = (patch: Partial<SessionData>) =>
    setData((d) => ({ ...d, ...patch }));

  const advance = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => {
    if (stepIdx === 0) onClose();
    else setStepIdx((i) => i - 1);
  };

  const current: StepKey = STEPS[stepIdx].key;

  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", `wizard_${current}`, {
        wizard_step: stepIdx + 1,
        wizard_title: STEPS[stepIdx].title,
      });
    }
  }, [stepIdx]);

  return (
    <div className="wizard">
      <header className="wizard-header">
        <button type="button" className="wizard-back" onClick={back}>
          ‹ {stepIdx === 0 ? "Cancel" : "Back"}
        </button>
        <h1 className="wizard-title">{STEPS[stepIdx].title}</h1>
        <span className="wizard-header-spacer" />
      </header>

      <StepIndicator current={current} />

      <div className="wizard-body">
        {current === "contact" && (
          <ContactInfoStep data={data} update={update} onNext={advance} />
        )}
        {current === "documents" && (
          <DocumentUploadStep
            data={data}
            update={update}
            onNext={advance}
            onSkip={advance}
          />
        )}
        {current === "review" && <ReviewStep data={data} onNext={advance} />}
        {current === "how" && <HowItWorksStep onNext={advance} />}
        {current === "confirmation" && (
          <ConfirmationStep data={data} onDone={onClose} />
        )}
      </div>
    </div>
  );
}
